import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Calendar, Clock, Phone, User, CheckCircle, Edit, Trash2, RotateCcw } from 'lucide-react';
import CreateReminderDialog from './CreateReminderDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgendaModal = ({ isOpen, onClose }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today'); // Default to today's reminders
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReminders();
    }
  }, [isOpen, filter]);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build URL with appropriate parameters
      let url = `${BACKEND_URL}/api/reminders`;
      const params = new URLSearchParams();
      
      // Handle different filter types
      if (filter === 'today') {
        params.append('filter_type', 'today');
      } else if (filter === 'overdue') {
        params.append('filter_type', 'overdue');
      } else if (filter === 'completed') {
        params.append('status', 'completed');
      } else if (filter === 'pending') {
        params.append('status', 'pending');
      }
      // 'all' doesn't need any params
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Sort by callback datetime
        data.sort((a, b) => new Date(a.callback_datetime) - new Date(b.callback_datetime));
        setReminders(data);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsComplete = async (reminderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/reminders/${reminderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'completed' })
      });

      if (response.ok) {
        alert('✅ Rappel marqué comme complété');
        fetchReminders();
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const deleteReminder = async (reminderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rappel?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/reminders/${reminderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Rappel supprimé');
        fetchReminders();
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700'
    };
    const emojis = { low: '🔵', medium: '🟡', high: '🔴' };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[priority]}`}>
        {emojis[priority]} {priority?.toUpperCase()}
      </span>
    );
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpcoming = (callbackDatetime) => {
    const now = new Date();
    const callback = new Date(callbackDatetime);
    return callback > now;
  };

  const getTimeUntil = (callbackDatetime) => {
    const now = new Date();
    const callback = new Date(callbackDatetime);
    const diff = callback - now;
    
    if (diff < 0) return 'Passé';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Dans ${days} jour${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `Dans ${hours}h ${minutes}m`;
    }
    return `Dans ${minutes}m`;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[900px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Agenda - Rappels Clients
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-2 items-center flex-wrap">
              <Button
                size="sm"
                variant={filter === 'today' ? 'default' : 'outline'}
                onClick={() => setFilter('today')}
                className={filter === 'today' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                🔥 Aujourd'hui
              </Button>
              <Button
                size="sm"
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
              >
                À venir
              </Button>
              <Button
                size="sm"
                variant={filter === 'overdue' ? 'default' : 'outline'}
                onClick={() => setFilter('overdue')}
                className={filter === 'overdue' ? 'bg-red-500 hover:bg-red-600' : ''}
              >
                ⚠️ En retard
              </Button>
              <Button
                size="sm"
                variant={filter === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilter('completed')}
              >
                ✅ Complétés
              </Button>
              <Button
                size="sm"
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
              >
                Tous
              </Button>
              
              <div className="ml-auto">
                <Button onClick={() => setShowCreateDialog(true)}>
                  + Nouveau Rappel
                </Button>
              </div>
            </div>

            {/* Reminders List */}
            <div className="overflow-y-auto max-h-[500px] space-y-3">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Chargement...</div>
              ) : reminders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Aucun rappel {
                    filter === 'today' ? "pour aujourd'hui" :
                    filter === 'pending' ? 'à venir' : 
                    filter === 'overdue' ? 'en retard' :
                    filter === 'completed' ? 'complété' : ''
                  }
                </div>
              ) : (
                reminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{reminder.customer_name}</h3>
                          {getPriorityBadge(reminder.priority)}
                          {reminder.status === 'completed' && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                              ✅ Complété
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {reminder.customer_phone}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {formatDateTime(reminder.callback_datetime)}
                            {reminder.status === 'pending' && (
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
                                isUpcoming(reminder.callback_datetime) ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {getTimeUntil(reminder.callback_datetime)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {reminder.created_by}
                          </div>
                          {reminder.notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                              <strong>Notes:</strong> {reminder.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      {reminder.status === 'pending' && (
                        <div className="flex gap-1 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsComplete(reminder.id)}
                            title="Marquer comme complété"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteReminder(reminder.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Reminder Dialog */}
      <CreateReminderDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        lead={null}
        onSuccess={fetchReminders}
      />
    </>
  );
};

export default AgendaModal;
